import { RefObject, useCallback, useLayoutEffect, useRef, useState } from "react";

import { OUTCOMES_TOUR_GAP_PX, OUTCOMES_TOUR_GLIDE_MS, OUTCOMES_TOUR_SCROLL_QUIET_MS, OutcomesTourStep } from "consts";

import {
  CardPlacement,
  HighlightBox,
  TourLayout,
  cardPositionFor,
  clipTopOf,
  easeInOutCubic,
  highlightBoxOf,
  isInBand,
  layoutBetween,
  placementFor,
  toWholePixels,
} from "pages/app/scenario-analysis/utils/outcomesTourLayout";
import { isRendered } from "pages/app/scenario-analysis/utils/outcomesTourTarget";
import { scrollParentBy } from "pages/app/scenario-analysis/utils/scrollUtils";

export interface UseOutcomesTourGlideOptions {
  step: OutcomesTourStep;
  target: HTMLElement | null;
  hasGivenUpOnTarget: boolean;
  hasLostTarget: boolean;
  cardRef: RefObject<HTMLElement>;
  highlightRef: RefObject<HTMLElement>;
  /** The sticky tab and settings bar: areas scroll clear of it and the band is never drawn over it. */
  stickyBarRef: RefObject<HTMLElement>;
}

const prefersReducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

const viewportSize = () => ({ width: window.innerWidth, height: window.innerHeight });

/** Scrolls each step's area clear of the sticky bar, and glides the card and band over to it from the last step. */
export const useOutcomesTourGlide = ({ step, target, hasGivenUpOnTarget, hasLostTarget, cardRef, highlightRef, stickyBarRef }: UseOutcomesTourGlideOptions) => {
  // What is on screen now, so the next step glides from it.
  const drawnRef = useRef<TourLayout | null>(null);
  const glideFrameRef = useRef<number | null>(null);
  // Chosen once per step from where the area will land, so the card never switches sides mid-scroll.
  const placementRef = useRef<CardPlacement>("corner");
  const [isShown, setIsShown] = useState(false);

  const stickyBarBottom = useCallback(() => {
    const bar = stickyBarRef.current;
    // The band may cover the bar only when the area is inside it.
    if (!bar || (target && bar.contains(target))) return 0;
    return bar.getBoundingClientRect().bottom;
  }, [stickyBarRef, target]);

  // Null while the area is hidden: a closed tab's area measures as a 0×0 box at the corner.
  const measureTargetBox = useCallback((): HighlightBox | null => {
    if (!target || !isRendered(target)) return null;
    return highlightBoxOf(target.getBoundingClientRect(), step, parseFloat(window.getComputedStyle(target).borderTopLeftRadius) || 0);
  }, [target, step]);

  const choosePlacement = useCallback(
    (box: HighlightBox | null) => {
      placementRef.current = placementFor(box, cardRef.current?.offsetHeight ?? 0, stickyBarBottom(), window.innerHeight);
    },
    [cardRef, stickyBarBottom]
  );

  const measure = useCallback((): TourLayout | null => {
    const card = cardRef.current;
    if (!card || (target && !isRendered(target))) return null;
    const box = measureTargetBox();
    const cardSize = { width: card.offsetWidth, height: card.offsetHeight };
    return { box, card: cardPositionFor(box, placementRef.current, cardSize, viewportSize(), stickyBarBottom()) };
  }, [cardRef, target, measureTargetBox, stickyBarBottom]);

  const hideBand = useCallback(() => {
    if (highlightRef.current) highlightRef.current.style.opacity = "0";
  }, [highlightRef]);

  // Straight to the DOM, as a render per frame would stutter; resizing one fixed band beats drawing it from transformed edges.
  const draw = useCallback(
    (layout: TourLayout) => {
      drawnRef.current = layout;
      const card = cardRef.current;
      const highlight = highlightRef.current;
      if (card) card.style.transform = `translate(${toWholePixels(layout.card.left)}, ${toWholePixels(layout.card.top)})`;
      if (!highlight) return;
      const { box } = layout;
      const clipTop = box ? clipTopOf(box, stickyBarBottom()) : 0;
      // Without an area, or with all of it under the sticky bar, the band fades out where it was.
      if (!box || clipTop >= box.height) {
        hideBand();
        return;
      }
      highlight.style.opacity = "1";
      highlight.style.transform = `translate(${toWholePixels(box.left)}, ${toWholePixels(box.top)})`;
      highlight.style.width = toWholePixels(box.width);
      highlight.style.height = toWholePixels(box.height);
      highlight.style.borderRadius = toWholePixels(box.radius);
      highlight.style.clipPath = clipTop > 0 ? `inset(${toWholePixels(clipTop)} 0 0 0)` : "none";
    },
    [cardRef, highlightRef, stickyBarBottom, hideBand]
  );

  useLayoutEffect(() => {
    if (!hasLostTarget) return;
    hideBand();
    if (drawnRef.current) drawnRef.current = { ...drawnRef.current, box: null };
  }, [hasLostTarget, hideBand]);

  useLayoutEffect(() => {
    if (!target && !hasGivenUpOnTarget) return undefined;
    const reduceMotion = prefersReducedMotion();

    // Re-measures every frame, so the glide lands exactly where the scroll leaves the area.
    const glide = () => {
      if (glideFrameRef.current !== null) cancelAnimationFrame(glideFrameRef.current);
      const from = drawnRef.current;
      const startedAt = performance.now();
      const frame = () => {
        const to = measure();
        if (!to) {
          glideFrameRef.current = null;
          return;
        }
        const progress = !from || reduceMotion ? 1 : Math.min(1, (performance.now() - startedAt) / OUTCOMES_TOUR_GLIDE_MS);
        draw(progress === 1 || !from ? to : layoutBetween(from, to, easeInOutCubic(progress)));
        glideFrameRef.current = progress === 1 ? null : requestAnimationFrame(frame);
      };
      frame();
    };

    // Scroll the area to just below the sticky bar, and pick the card's side for where the area will land.
    const box = measureTargetBox();
    const barBottom = stickyBarBottom();
    const rawDistance = box && barBottom ? box.top - barBottom - OUTCOMES_TOUR_GAP_PX : 0;
    const distance = Math.abs(rawDistance) < 2 ? 0 : rawDistance;
    choosePlacement(box ? { ...box, top: box.top - distance } : null);
    if (target && distance) scrollParentBy(target, distance, reduceMotion ? "auto" : "smooth");
    glide();
    setIsShown(true);

    // Once the scroll goes quiet (or never starts, on a short page) the side is re-picked from where the area really landed.
    let hasSettled = !distance;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;
    const settle = () => {
      hasSettled = true;
      const placement = placementRef.current;
      choosePlacement(measureTargetBox());
      if (placementRef.current !== placement) glide();
    };
    const settleOnceQuiet = () => {
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, OUTCOMES_TOUR_SCROLL_QUIET_MS);
    };
    if (!hasSettled) settleOnceQuiet();

    // After the glide, the step follows the farmer's own scrolling and resizing.
    let followFrame: number | null = null;
    const follow = () => {
      if (glideFrameRef.current !== null || followFrame !== null) return;
      followFrame = requestAnimationFrame(() => {
        followFrame = null;
        const layout = measure();
        if (layout) draw(layout);
      });
    };
    const handleScroll = () => {
      follow();
      if (!hasSettled) settleOnceQuiet();
    };
    const handleResize = () => {
      choosePlacement(measureTargetBox());
      follow();
    };
    // Capture catches scrolling in the page's own scroll container, not just the window.
    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(follow);
    if (target) resizeObserver?.observe(target);
    if (cardRef.current) resizeObserver?.observe(cardRef.current);
    return () => {
      if (glideFrameRef.current !== null) cancelAnimationFrame(glideFrameRef.current);
      glideFrameRef.current = null;
      if (followFrame !== null) cancelAnimationFrame(followFrame);
      if (settleTimer) clearTimeout(settleTimer);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
      resizeObserver?.disconnect();
    };
  }, [target, hasGivenUpOnTarget, step, cardRef, stickyBarBottom, measureTargetBox, choosePlacement, measure, draw]);

  /** Whether a point is on the visible band: a press on the sticky bar above it is outside. */
  const isOnBand = useCallback(
    (x: number, y: number) => {
      const box = measureTargetBox();
      return box !== null && isInBand(box, stickyBarBottom(), x, y);
    },
    [measureTargetBox, stickyBarBottom]
  );

  return { isShown, isOnBand };
};
